import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Clock,
  MapPin,
  Star,
  Camera,
  ForkKnife,
  Users,
  SpinnerGap,
  Heart,
  BookOpen,
  CalendarBlank,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VIBE_COLORS = {
  romantic: 'bg-pink-100 text-pink-700',
  celebration: 'bg-amber-100 text-amber-700',
  casual: 'bg-sky-100 text-sky-700',
  business: 'bg-slate-100 text-slate-700',
  family: 'bg-green-100 text-green-700',
  adventure: 'bg-orange-100 text-orange-700',
};

const Timeline = () => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchTimeline(); }, [page]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/timeline?page=${page}&limit=15`);
      setEntries(res.data.entries || []);
      setTotalPages(res.data.pages || 1);
    } catch { toast.error('Failed to load timeline'); }
    finally { setLoading(false); }
  };

  if (loading && page === 1) {
    return (
      <div className="container-app py-8 pb-8 flex justify-center pt-32">
        <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
      </div>
    );
  }

  return (
    <div className="container-app py-8 pb-8 max-w-2xl mx-auto" data-testid="timeline-page">
      <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918] mb-2" data-testid="timeline-title">
        Your Dining Story
      </h1>
      <p className="text-[#7A7873] mb-8">Every meal tells a story</p>

      {entries.length === 0 && (
        <div className="empty-state">
          <Clock weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">No visits yet</h3>
          <p className="empty-state-text">Check in at a restaurant to start your timeline</p>
        </div>
      )}

      {/* Timeline line */}
      <div className="relative">
        {entries.length > 0 && (
          <div className="absolute left-6 top-0 bottom-0 w-px bg-[#EAE8E3]" />
        )}

        {entries.map((entry, idx) => {
          const photos = (entry.attachments || []).filter(a =>
            a.content_type?.startsWith('image/') || a.file_path?.match(/\.(jpg|jpeg|png|webp)$/i)
          );
          const photoUrl = photos.length > 0
            ? `${BACKEND_URL}/api${photos[0].file_path}`
            : null;

          return (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.03 }}
              className="relative pl-16 mb-8"
              data-testid={`timeline-entry-${entry.id}`}
            >
              {/* Timeline dot */}
              <div className="absolute left-4 top-2 w-5 h-5 rounded-full bg-white border-2 border-[#A94438] z-10" />

              {/* Card */}
              <Link
                to={`/restaurants/${entry.restaurant_id}`}
                className="block rounded-2xl border border-[#EAE8E3] bg-white overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Photo banner */}
                {photoUrl && (
                  <div className="h-40 relative">
                    <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    {photos.length > 1 && (
                      <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs rounded-full px-2.5 py-1 flex items-center gap-1">
                        <Camera weight="fill" className="w-3 h-3" />
                        {photos.length}
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4">
                  {/* Date */}
                  <div className="flex items-center gap-2 text-xs text-[#7A7873] mb-2">
                    <CalendarBlank weight="duotone" className="w-3.5 h-3.5" />
                    {new Date(entry.visited_at).toLocaleDateString('en-ZA', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    })}
                    {entry.meal_type && (
                      <span className="ml-auto text-[#A94438] font-medium capitalize">{entry.meal_type}</span>
                    )}
                  </div>

                  {/* Restaurant name */}
                  <h3 className="font-heading font-semibold text-lg text-[#1A1918] mb-1">
                    {entry.restaurant_name}
                  </h3>
                  {entry.restaurant_suburb && (
                    <div className="flex items-center gap-1 text-sm text-[#7A7873] mb-2">
                      <MapPin weight="duotone" className="w-3.5 h-3.5" />
                      {entry.restaurant_suburb}
                    </div>
                  )}

                  {/* Vibe + companions */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {entry.vibe && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${VIBE_COLORS[entry.vibe] || 'bg-gray-100 text-gray-700'}`}>
                        {entry.vibe}
                      </span>
                    )}
                    {(entry.companions || []).length > 0 && (
                      <span className="text-xs text-[#7A7873] flex items-center gap-1">
                        <Users weight="duotone" className="w-3.5 h-3.5" />
                        with {entry.companions.join(', ')}
                      </span>
                    )}
                  </div>

                  {/* Rating */}
                  {entry.overall_rating && (
                    <div className="flex items-center gap-1 text-[#C4A35A] text-sm mb-2">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} weight={s <= entry.overall_rating ? 'fill' : 'regular'} className="w-4 h-4" />
                      ))}
                    </div>
                  )}

                  {/* Journal / Notes */}
                  {(entry.journal_entry || entry.notes) && (
                    <p className="text-sm text-[#4A4845] line-clamp-2 leading-relaxed">
                      {entry.journal_entry || entry.notes}
                    </p>
                  )}

                  {/* Favourite dishes */}
                  {(entry.favourite_dishes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {entry.favourite_dishes.map((d, i) => (
                        <span key={i} className="text-xs bg-[#D1E2C4]/50 text-[#3E4A39] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Heart weight="fill" className="w-3 h-3 text-[#A94438]" />
                          {d}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6" data-testid="timeline-pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="filter-pill"
          >Previous</button>
          <span className="text-sm text-[#7A7873]">{page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="filter-pill"
          >Next</button>
        </div>
      )}
    </div>
  );
};

export default Timeline;
